import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { reminderApi, memberApi } from '../../services/api';

const ReminderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    enabled: true,
    reminder_type: 'email',
    frequency: 'monthly',
    trigger: 'days_before_expiry',
    trigger_days: 7,
    template_id: '',
    send_time: '09:00'
  });
  
  // Additional state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch templates
        const templatesResponse = await reminderApi.getAllTemplates();
        setTemplates(templatesResponse.data);
        
        // Set default template if available
        if (templatesResponse.data.length > 0) {
          const defaultTemplate = templatesResponse.data.find(t => t.is_default) || templatesResponse.data[0];
          setFormData(prev => ({ ...prev, template_id: defaultTemplate.id }));
        }
        
        // Fetch members for testing
        const membersResponse = await memberApi.getAll();
        setMembers(membersResponse.data);
        
        // If in edit mode, fetch the reminder data
        if (isEditMode) {
          const reminderResponse = await reminderApi.getReminderById(id);
          const reminderData = reminderResponse.data;
          
          // Format send_time for input
          let sendTime = reminderData.send_time;
          if (sendTime && !sendTime.includes(':')) {
            sendTime = '09:00'; // Default if invalid format
          }
          
          setFormData({
            name: reminderData.name || '',
            enabled: reminderData.enabled !== false, // Default to true if not specified
            reminder_type: reminderData.reminder_type || 'email',
            frequency: reminderData.frequency || 'monthly',
            trigger: reminderData.trigger || 'days_before_expiry',
            trigger_days: reminderData.trigger_days || 7,
            template_id: reminderData.template_id || '',
            send_time: sendTime
          });
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode) {
        await reminderApi.updateReminder(id, formData);
      } else {
        await reminderApi.createReminder(formData);
      }
      navigate('/reminders');
    } catch (err) {
      console.error('Error saving reminder:', err);
      setError('Failed to save reminder. Please check your inputs and try again.');
      setSubmitting(false);
    }
  };
  
  // Handle test reminder
  const handleTestReminder = async () => {
    if (!selectedMember) {
      setTestResult({
        success: false,
        message: 'Please select a member to test with.'
      });
      return;
    }
    
    setTestLoading(true);
    setTestResult(null);
    
    try {
      if (isEditMode) {
        // For existing reminders, use the API endpoint
        await reminderApi.testReminder(id, selectedMember);
        setTestResult({
          success: true,
          message: 'Test reminder sent successfully!'
        });
      } else {
        // For new reminders, show a message that they need to save first
        setTestResult({
          success: false,
          message: 'Please save the reminder first before testing.'
        });
      }
    } catch (err) {
      console.error('Error testing reminder:', err);
      setTestResult({
        success: false,
        message: `Failed to send test reminder: ${err.response?.data?.detail || err.message}`
      });
    } finally {
      setTestLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="reminder-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isEditMode ? 'Edit Reminder' : 'New Reminder'}</h1>
        <Link to="/reminders" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-1"></i> Back to Reminders
        </Link>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="name" className="form-label">Reminder Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="reminder_type" className="form-label">Reminder Type</label>
                <select
                  className="form-select"
                  id="reminder_type"
                  name="reminder_type"
                  value={formData.reminder_type}
                  onChange={handleChange}
                  required
                >
                  <option value="email">Email</option>
                  <option value="telegram">Telegram</option>
                </select>
              </div>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="frequency" className="form-label">Frequency</label>
                <select
                  className="form-select"
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleChange}
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="once">Once</option>
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor="send_time" className="form-label">Send Time</label>
                <input
                  type="time"
                  className="form-control"
                  id="send_time"
                  name="send_time"
                  value={formData.send_time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="trigger" className="form-label">Trigger</label>
                <select
                  className="form-select"
                  id="trigger"
                  name="trigger"
                  value={formData.trigger}
                  onChange={handleChange}
                  required
                >
                  <option value="days_before_expiry">Days Before Expiry</option>
                  <option value="days_after_expiry">Days After Expiry</option>
                  <option value="specific_day">Specific Day of Month</option>
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor="trigger_days" className="form-label">
                  {formData.trigger === 'specific_day' ? 'Day of Month' : 'Number of Days'}
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="trigger_days"
                  name="trigger_days"
                  value={formData.trigger_days}
                  onChange={handleChange}
                  min={formData.trigger === 'specific_day' ? 1 : 0}
                  max={formData.trigger === 'specific_day' ? 31 : 365}
                  required
                />
              </div>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="template_id" className="form-label">Template</label>
                <select
                  className="form-select"
                  id="template_id"
                  name="template_id"
                  value={formData.template_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  <Link to="/reminder-templates/new">Create a new template</Link>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="enabled"
                    name="enabled"
                    checked={formData.enabled}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="enabled">
                    Enable this reminder
                  </label>
                </div>
              </div>
            </div>
            
            <div className="d-flex justify-content-between mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-1"></i> {isEditMode ? 'Update' : 'Create'} Reminder
                  </>
                )}
              </button>
              
              {isEditMode && (
                <div className="d-flex align-items-center">
                  <select
                    className="form-select me-2"
                    value={selectedMember}
                    onChange={(e) => setSelectedMember(e.target.value)}
                    disabled={testLoading}
                  >
                    <option value="">Select a member to test</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name || member.username}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-info"
                    onClick={handleTestReminder}
                    disabled={testLoading || !selectedMember}
                  >
                    {testLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Testing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-vial me-1"></i> Test Reminder
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </form>
          
          {testResult && (
            <div className={`alert mt-3 ${testResult.success ? 'alert-success' : 'alert-danger'}`} role="alert">
              {testResult.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReminderForm;
